/**
 * SourcesPanel — pluggable "import notes from..." UI for the proposal builder.
 *
 * Walks the backend's /sources endpoint to discover which external note
 * adapters are wired (Granola, Parkify Notetaker, future ones). For
 * each adapter shows one of two modes:
 *
 *   - **Configured** → dropdown of recent notes; pick → fetch → insert
 *     into the notes textarea with a "--- {Label} ---" separator.
 *
 *   - **Not configured** → a paste-in textarea; paste → insert with the
 *     same separator. Works today even without the API keys, so the
 *     rep can immediately pull Granola summaries by copy/paste while
 *     we wait on per-rep OAuth / workspace API access.
 *
 * The panel is intentionally additive — it never touches the notes
 * textarea contents on its own. The caller passes `onInsert(text)` and
 * decides where to put it.
 */
import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import {
  fetchSourceNote,
  listSourceNotes,
  listSources,
  type SourceNoteSummary,
  type SourceStatus,
} from '../api/sequences'
import { colors } from '../theme'

interface Props {
  onInsert: (textWithSeparator: string) => void
}

export default function SourcesPanel({ onInsert }: Props) {
  const { apiBase, authHeaders } = useAuth()
  const [sources, setSources] = useState<SourceStatus[] | null>(null)
  const [openSource, setOpenSource] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    listSources(apiBase, authHeaders())
      .then((rows) => !cancelled && setSources(rows))
      .catch(() => !cancelled && setSources([]))
    return () => {
      cancelled = true
    }
  }, [apiBase, authHeaders])

  if (sources === null) return null
  if (sources.length === 0) return null

  return (
    <div
      style={{
        background: colors.bgAlt,
        border: `1px solid ${colors.divider}`,
        borderRadius: 10,
        padding: '12px 14px',
        marginTop: 12,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '2px',
          color: colors.purple,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        Pull notes from
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {sources.map((s) => (
          <button
            key={s.name}
            onClick={() => setOpenSource(openSource === s.name ? null : s.name)}
            style={{
              background: openSource === s.name ? colors.purple : colors.white,
              color: openSource === s.name ? colors.white : colors.black,
              border: `1px solid ${
                openSource === s.name ? colors.purple : colors.divider
              }`,
              borderRadius: 999,
              padding: '6px 14px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            {s.label}
            <span
              style={{
                marginLeft: 6,
                fontSize: 9,
                opacity: 0.7,
              }}
            >
              {s.configured ? 'connected' : 'paste-in'}
            </span>
          </button>
        ))}
      </div>
      {openSource ? (
        <SourceEditor
          key={openSource}
          source={sources.find((s) => s.name === openSource)!}
          onInsert={(text) => {
            onInsert(text)
            setOpenSource(null)
            setError(null)
          }}
          onError={setError}
        />
      ) : null}
      {error ? (
        <div
          style={{
            color: colors.red,
            fontSize: 12,
            marginTop: 8,
          }}
        >
          {error}
        </div>
      ) : null}
    </div>
  )
}

function SourceEditor({
  source,
  onInsert,
  onError,
}: {
  source: SourceStatus
  onInsert: (text: string) => void
  onError: (msg: string | null) => void
}) {
  const { apiBase, authHeaders } = useAuth()
  const [notes, setNotes] = useState<SourceNoteSummary[] | null>(null)
  const [picked, setPicked] = useState<string>('')
  const [pasted, setPasted] = useState('')
  const [busy, setBusy] = useState(false)

  // Load the recent-notes dropdown when the source claims to be wired
  // up. Unconfigured sources skip the fetch entirely.
  useEffect(() => {
    if (!source.configured) return
    let cancelled = false
    listSourceNotes(apiBase, authHeaders(), source.name)
      .then((r) => !cancelled && setNotes(r.notes))
      .catch(() => !cancelled && setNotes([]))
    return () => {
      cancelled = true
    }
  }, [apiBase, authHeaders, source])

  const separator = useMemo(
    () => `\n\n--- ${source.label} summary ---\n`,
    [source.label],
  )

  async function importFromApi() {
    if (!picked) return
    setBusy(true)
    onError(null)
    try {
      const n = await fetchSourceNote(
        apiBase,
        authHeaders(),
        source.name,
        picked,
      )
      const body = [
        n.title ? `Title: ${n.title}` : '',
        n.occurred_at ? `When: ${n.occurred_at}` : '',
        n.participants?.length ? `Participants: ${n.participants.join(', ')}` : '',
        n.summary ? `\nSummary:\n${n.summary}` : '',
        n.action_items?.length
          ? `\nAction items:\n- ${n.action_items.join('\n- ')}`
          : '',
        n.transcript ? `\nTranscript:\n${n.transcript}` : '',
      ]
        .filter(Boolean)
        .join('\n')
      onInsert(separator + body.trim())
      setPicked('')
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Import failed')
    } finally {
      setBusy(false)
    }
  }

  function insertPaste() {
    if (!pasted.trim()) return
    onInsert(separator + pasted.trim())
    setPasted('')
  }

  return (
    <div
      style={{
        marginTop: 12,
        background: colors.white,
        border: `1px solid ${colors.divider}`,
        borderRadius: 8,
        padding: 12,
      }}
    >
      {source.configured ? (
        <div>
          <div
            style={{
              fontSize: 11,
              color: colors.textMuted,
              marginBottom: 8,
            }}
          >
            Pick a recent {source.label} note to insert into the proposal
            notes below.
          </div>
          {notes === null ? (
            <div style={{ color: colors.textMuted, fontSize: 12 }}>
              Loading…
            </div>
          ) : notes.length === 0 ? (
            <div style={{ color: colors.textMuted, fontSize: 12 }}>
              No recent notes returned. Try the paste-in path below.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select
                value={picked}
                onChange={(e) => setPicked(e.target.value)}
                style={{
                  flex: 1,
                  padding: '8px 10px',
                  fontSize: 13,
                  border: `1px solid ${colors.divider}`,
                  borderRadius: 8,
                  background: colors.white,
                  color: colors.black,
                  minWidth: 240,
                }}
              >
                <option value="">— pick a {source.label} note —</option>
                {notes.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.title}
                    {n.occurred_at ? ` · ${formatDateShort(n.occurred_at)}` : ''}
                  </option>
                ))}
              </select>
              <button
                onClick={importFromApi}
                disabled={!picked || busy}
                style={{
                  background: colors.purple,
                  color: colors.white,
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '1.2px',
                  textTransform: 'uppercase',
                  cursor: !picked || busy ? 'not-allowed' : 'pointer',
                  opacity: !picked || busy ? 0.5 : 1,
                }}
              >
                {busy ? 'Importing…' : 'Insert into notes'}
              </button>
            </div>
          )}
          <div
            style={{
              fontSize: 11,
              color: colors.textMuted,
              marginTop: 10,
              borderTop: `1px solid ${colors.divider}`,
              paddingTop: 10,
            }}
          >
            Or paste a {source.label} summary directly:
          </div>
        </div>
      ) : (
        <div
          style={{
            fontSize: 11,
            color: colors.textMuted,
            marginBottom: 8,
          }}
        >
          {source.label} API isn't wired on this deploy yet. Paste a
          summary below and we'll fold it into the proposal notes — the
          downstream LLM treats it the same way it'll treat the API
          fetch once that's live.
        </div>
      )}
      <textarea
        value={pasted}
        onChange={(e) => setPasted(e.target.value)}
        rows={5}
        placeholder={`Paste your ${source.label} summary or transcript here…`}
        style={{
          width: '100%',
          padding: '10px 12px',
          background: colors.bgAlt,
          color: colors.black,
          border: `1px solid ${colors.divider}`,
          borderRadius: 8,
          fontSize: 13,
          lineHeight: 1.55,
          fontFamily: 'inherit',
          resize: 'vertical',
          boxSizing: 'border-box',
          marginTop: 8,
        }}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 8,
        }}
      >
        <button
          onClick={insertPaste}
          disabled={!pasted.trim()}
          style={{
            background: colors.white,
            color: colors.purple,
            border: `1px solid ${colors.purple}`,
            borderRadius: 8,
            padding: '6px 14px',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
            cursor: !pasted.trim() ? 'not-allowed' : 'pointer',
            opacity: !pasted.trim() ? 0.5 : 1,
          }}
        >
          Insert into notes
        </button>
      </div>
    </div>
  )
}

function formatDateShort(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
}
